let bmb17TrackScriptPromise;
let bmbTurnstileScriptPromise;

function loadBmb17Track() {
  if (window.YQV5?.trackSingle) return Promise.resolve(window.YQV5);
  if (bmb17TrackScriptPromise) return bmb17TrackScriptPromise;

  bmb17TrackScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-bmb-17track]');
    const script = existingScript || document.createElement('script');
    const timeout = window.setTimeout(() => reject(new Error('17TRACK timed out.')), 12000);

    const handleLoad = () => {
      window.clearTimeout(timeout);
      if (window.YQV5?.trackSingle) {
        resolve(window.YQV5);
      } else {
        reject(new Error('17TRACK did not initialize.'));
      }
    };

    const handleError = () => {
      window.clearTimeout(timeout);
      reject(new Error('17TRACK could not be loaded.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = 'https://www.17track.net/externalcall.js';
      script.async = true;
      script.dataset.bmb17track = 'true';
      document.head.append(script);
    }
  }).catch((error) => {
    bmb17TrackScriptPromise = undefined;
    throw error;
  });

  return bmb17TrackScriptPromise;
}

function loadBmbTurnstile() {
  if (window.turnstile?.render) return Promise.resolve(window.turnstile);
  if (bmbTurnstileScriptPromise) return bmbTurnstileScriptPromise;

  bmbTurnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-bmb-turnstile]');
    const script = existingScript || document.createElement('script');
    const timeout = window.setTimeout(() => reject(new Error('Security check timed out.')), 12000);

    const handleLoad = () => {
      window.clearTimeout(timeout);
      if (window.turnstile?.render) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Security check did not initialize.'));
      }
    };

    const handleError = () => {
      window.clearTimeout(timeout);
      reject(new Error('Security check could not be loaded.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    if (!existingScript) {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.bmbTurnstile = 'true';
      document.head.append(script);
    }
  }).catch((error) => {
    bmbTurnstileScriptPromise = undefined;
    throw error;
  });

  return bmbTurnstileScriptPromise;
}

class BmbOrderTracking extends HTMLElement {
  #tabs = [];
  #panels = [];
  #forms = [];
  #result;
  #liveTracking;
  #liveTrackingContainer;
  #liveTrackingLoading;
  #turnstileContainer;
  #turnstileWidgetId;
  #turnstilePending;
  #requestController;

  connectedCallback() {
    this.#tabs = [...this.querySelectorAll('[data-tracking-tab]')];
    this.#panels = [...this.querySelectorAll('[data-tracking-panel]')];
    this.#forms = [...this.querySelectorAll('[data-tracking-form]')];
    this.#result = this.querySelector('[data-tracking-result]');
    this.#liveTracking = this.querySelector('[data-17track-result]');
    this.#liveTrackingContainer = this.querySelector('[data-17track-container]');
    this.#liveTrackingLoading = this.querySelector('[data-17track-loading]');
    this.#turnstileContainer = this.querySelector('[data-turnstile-container]');

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#handleTabClick);
      tab.addEventListener('keydown', this.#handleTabKeydown);
    }

    for (const form of this.#forms) {
      form.addEventListener('submit', this.#handleSubmit);
      for (const input of form.querySelectorAll('input')) {
        input.addEventListener('input', this.#clearFieldError);
      }
    }
  }

  disconnectedCallback() {
    this.#requestController?.abort();
    this.#rejectTurnstile(new Error('Security check cancelled.'));

    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#handleTabClick);
      tab.removeEventListener('keydown', this.#handleTabKeydown);
    }

    for (const form of this.#forms) {
      form.removeEventListener('submit', this.#handleSubmit);
      for (const input of form.querySelectorAll('input')) {
        input.removeEventListener('input', this.#clearFieldError);
      }
    }
  }

  #handleTabClick = (event) => {
    this.#activateTab(event.currentTarget);
  };

  #handleTabKeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.#tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? this.#tabs.length - 1 : currentIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = currentIndex === this.#tabs.length - 1 ? 0 : currentIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#tabs.length - 1;

    this.#activateTab(this.#tabs[nextIndex]);
    this.#tabs[nextIndex].focus();
  };

  #activateTab(tabToActivate) {
    const panelId = tabToActivate.getAttribute('aria-controls');

    for (const tab of this.#tabs) {
      const isActive = tab === tabToActivate;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.id !== panelId;
    }
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    this.#hideResults();
    if (!this.#validateForm(form)) return;

    const formData = new FormData(form);
    const trackingNumber = String(formData.get('tracking_number') || '').trim();

    if (trackingNumber) {
      if (!/^[A-Z0-9][A-Z0-9 ._\/-]{4,49}$/i.test(trackingNumber)) {
        this.#showLookupError(form, form.dataset.invalidTrackingMessage);
        return;
      }

      await this.#showLiveTracking(form, trackingNumber);
      return;
    }

    if (form.hasAttribute('data-order-lookup-form')) {
      await this.#lookupOrder(form, formData);
    }
  };

  async #lookupOrder(form, formData) {
    const button = form.querySelector('button[type="submit"]');
    this.#setButtonLoading(button, true);
    this.#requestController?.abort();
    this.#requestController = new AbortController();

    try {
      const turnstileToken = await this.#getTurnstileToken();
      const response = await fetch(this.dataset.lookupEndpoint || form.action, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderNumber: String(formData.get('order') || '').trim(),
          identity: String(formData.get('identity') || '').trim(),
          company: String(formData.get('company') || '').trim(),
          turnstileToken,
        }),
        signal: this.#requestController.signal,
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new BmbTrackingResponseError(payload?.message || form.dataset.serviceErrorMessage);
      }

      if (payload?.found !== true || !payload.order) {
        this.#showLookupError(form, payload?.message || form.dataset.notFoundMessage);
        return;
      }

      const shipment = this.#renderOrder(payload.order);
      if (shipment?.number) {
        await this.#showLiveTracking(form, shipment.number, { scroll: false });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const message = error instanceof BmbTrackingResponseError
        ? error.message
        : form.dataset.serviceErrorMessage;
      this.#showLookupError(form, message);
    } finally {
      this.#setButtonLoading(button, false);
      this.#requestController = undefined;
      this.#resetTurnstile();
    }
  }

  async #getTurnstileToken() {
    const siteKey = String(this.dataset.turnstileSiteKey || '').trim();
    if (!siteKey) return '';
    if (!this.#turnstileContainer) throw new Error('Security check is unavailable.');

    const turnstile = await loadBmbTurnstile();
    this.#turnstileContainer.hidden = false;

    if (this.#turnstileWidgetId === undefined) {
      this.#turnstileWidgetId = turnstile.render(this.#turnstileContainer, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        execution: 'execute',
        size: 'flexible',
        callback: (token) => this.#resolveTurnstile(token),
        'error-callback': () => this.#rejectTurnstile(new Error('Security check failed.')),
        'expired-callback': () => this.#rejectTurnstile(new Error('Security check expired.')),
        'timeout-callback': () => this.#rejectTurnstile(new Error('Security check timed out.')),
      });
    }

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.#rejectTurnstile(new Error('Security check timed out.'));
      }, 15000);

      this.#turnstilePending = { resolve, reject, timeout };
      turnstile.execute(this.#turnstileWidgetId);
    });
  }

  #resolveTurnstile(token) {
    if (!this.#turnstilePending) return;
    const { resolve, timeout } = this.#turnstilePending;
    window.clearTimeout(timeout);
    this.#turnstilePending = undefined;
    resolve(token);
  }

  #rejectTurnstile(error) {
    if (!this.#turnstilePending) return;
    const { reject, timeout } = this.#turnstilePending;
    window.clearTimeout(timeout);
    this.#turnstilePending = undefined;
    reject(error);
  }

  #resetTurnstile() {
    if (this.#turnstileWidgetId === undefined || !window.turnstile?.reset) return;
    window.turnstile.reset(this.#turnstileWidgetId);
  }

  #renderOrder(order) {
    if (!this.#result) return;

    const status = this.#supportedStatus(order.status);
    const shipment = this.#selectShipment(order.shipments);
    const orderNumber = this.querySelector('[data-result-order]');
    const statusElement = this.querySelector('[data-result-status]');
    const eta = this.querySelector('[data-result-eta]');
    const etaValue = this.querySelector('[data-result-eta-value]');
    const timeline = this.querySelector('.bmb-tracking__timeline-wrap');
    const useLiveCarrierStatus = Boolean(shipment?.number);

    if (orderNumber) orderNumber.textContent = this.#safeText(order.number, 40);
    if (statusElement) {
      statusElement.textContent = this.#statusLabel(status);
      statusElement.dataset.status = status;
      statusElement.hidden = useLiveCarrierStatus;
    }

    const estimatedDelivery = this.#formatDate(shipment?.estimatedDeliveryAt);
    if (eta && etaValue) {
      eta.hidden = !estimatedDelivery;
      etaValue.textContent = estimatedDelivery || '';
    }

    if (timeline) timeline.hidden = status === 'cancelled' || useLiveCarrierStatus;
    this.#renderSteps(status);
    this.#renderShipment(shipment);

    this.#result.hidden = false;
    this.#result.focus({ preventScroll: true });
    this.#result.scrollIntoView({
      behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    return shipment;
  }

  #renderSteps(status) {
    const steps = [...this.querySelectorAll('[data-result-step]')];
    const currentIndex = {
      processing: 0,
      shipped: 1,
      in_transit: 2,
      out_for_delivery: 2,
      delivered: 3,
    }[status];

    for (const [index, step] of steps.entries()) {
      step.classList.remove('bmb-tracking__step--done', 'bmb-tracking__step--current');
      if (currentIndex === undefined) continue;
      if (index < currentIndex) step.classList.add('bmb-tracking__step--done');
      if (index === currentIndex) step.classList.add('bmb-tracking__step--current');
    }
  }

  #renderShipment(shipment) {
    const shipmentMeta = this.querySelector('[data-result-shipment-meta]');
    const carrierWrap = this.querySelector('[data-result-carrier-wrap]');
    const trackingWrap = this.querySelector('[data-result-tracking-wrap]');
    const carrier = this.querySelector('[data-result-carrier]');
    const tracking = this.querySelector('[data-result-tracking]');
    const empty = this.querySelector('[data-result-shipment-empty]');
    const link = this.querySelector('[data-result-carrier-link]');
    const hasCarrier = Boolean(shipment?.company) && !/^(other|unknown)$/i.test(shipment.company.trim());
    const hasTracking = Boolean(shipment?.number);

    if (carrier) carrier.textContent = hasCarrier ? this.#safeText(shipment.company, 80) : '';
    if (tracking) tracking.textContent = hasTracking ? this.#safeText(shipment.number, 120) : '';
    if (carrierWrap) carrierWrap.hidden = !hasCarrier;
    if (trackingWrap) trackingWrap.hidden = !hasTracking;
    if (shipmentMeta) shipmentMeta.hidden = !hasCarrier && !hasTracking;
    if (empty) empty.hidden = hasCarrier || hasTracking;

    if (link) {
      const url = this.#trackingUrl(shipment);
      link.hidden = !url;
      if (url) {
        link.href = url;
      } else {
        link.removeAttribute('href');
      }
    }
  }

  async #showLiveTracking(form, trackingNumber, { scroll = true } = {}) {
    if (!this.#liveTracking || !this.#liveTrackingContainer) return;

    this.#liveTracking.hidden = false;
    this.#liveTrackingContainer.replaceChildren();
    if (this.#liveTrackingLoading) this.#liveTrackingLoading.hidden = false;
    if (scroll) {
      this.#liveTracking.focus({ preventScroll: true });
      this.#liveTracking.scrollIntoView({
        behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    try {
      const tracker = await loadBmb17Track();
      tracker.trackSingle({
        YQ_ContainerId: this.#liveTrackingContainer.id,
        YQ_Height: 560,
        YQ_Fc: '0',
        YQ_Lang: document.documentElement.lang?.split('-')[0] || 'en',
        YQ_Num: trackingNumber,
      });

      window.setTimeout(() => {
        if (this.#liveTrackingLoading) this.#liveTrackingLoading.hidden = true;
      }, 300);
    } catch {
      this.#liveTracking.hidden = true;
      this.#showLookupError(form, form.dataset.serviceErrorMessage);
    }
  }

  #hideResults() {
    if (this.#result) this.#result.hidden = true;
    if (this.#liveTracking) this.#liveTracking.hidden = true;
  }

  #showLookupError(form, message = '') {
    const input = form.querySelector('input[required]');
    const error = input ? form.querySelector(`[data-error-for="${input.id}"]`) : null;
    if (!input || !error) return;

    input.setAttribute('aria-invalid', 'true');
    error.textContent = message || form.dataset.notFoundMessage || '';
    error.hidden = false;
    input.focus();
  }

  #validateForm(form) {
    let isValid = true;
    let firstInvalidInput;

    for (const input of form.querySelectorAll('input[required]')) {
      const error = form.querySelector(`[data-error-for="${input.id}"]`);
      const value = input.value.trim();
      const fieldIsValid = value.length > 0;

      input.setAttribute('aria-invalid', String(!fieldIsValid));
      if (error) {
        error.hidden = fieldIsValid;
        error.textContent = input.dataset.requiredMessage || '';
      }

      if (!fieldIsValid) {
        isValid = false;
        firstInvalidInput ||= input;
      }
    }

    firstInvalidInput?.focus();
    return isValid;
  }

  #clearFieldError = (event) => {
    const input = event.currentTarget;
    const error = input.form?.querySelector(`[data-error-for="${input.id}"]`);
    input.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
  };

  #setButtonLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
    button.textContent = loading
      ? button.dataset.loadingLabel || 'Loading…'
      : button.dataset.submitLabel || 'Track my order';
  }

  #supportedStatus(value) {
    const status = String(value || '').toLowerCase();
    return ['processing', 'shipped', 'carrier_tracking', 'in_transit', 'out_for_delivery', 'delivered', 'cancelled'].includes(status)
      ? status
      : 'processing';
  }

  #statusLabel(status) {
    const key = status.replace(/_([a-z])/g, (_, character) => character.toUpperCase());
    return this.dataset[`status${key.charAt(0).toUpperCase()}${key.slice(1)}`] || status.replaceAll('_', ' ');
  }

  #selectShipment(shipments) {
    if (!Array.isArray(shipments)) return null;
    return shipments.find((shipment) => shipment?.number) || shipments[0] || null;
  }

  #trackingUrl(shipment) {
    const directUrl = this.#safeHttpUrl(shipment?.url);
    if (directUrl && !this.#isLegacyTrackingAppUrl(directUrl)) return directUrl;
    if (!shipment?.number) return '';
    return `https://t.17track.net/en#nums=${encodeURIComponent(this.#safeText(shipment.number, 120))}`;
  }

  #isLegacyTrackingAppUrl(value) {
    try {
      return /^\/apps\/tracking(?:\/|$)/i.test(new URL(value).pathname);
    } catch {
      return false;
    }
  }

  #safeHttpUrl(value) {
    try {
      const url = new URL(String(value || ''));
      return ['https:', 'http:'].includes(url.protocol) ? url.toString() : '';
    } catch {
      return '';
    }
  }

  #safeText(value, maxLength) {
    return typeof value === 'string' ? value.slice(0, maxLength) : '';
  }

  #formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

class BmbTrackingResponseError extends Error {}

if (!customElements.get('bmb-order-tracking')) {
  customElements.define('bmb-order-tracking', BmbOrderTracking);
}
